import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';
import AlertDialog from '../../Shared/AlertDialog';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import { FiPackage, FiBriefcase, FiShare2, FiInbox } from 'react-icons/fi';

const PAGE_SIZE = 10;

export default function CarrerasCompartidasCatalog() {
    const { puedeEditar } = useResourcePermissions('LMS_Carreras_Compartidas');
    const [loading, setLoading] = useState(false);
    const [misCarreras, setMisCarreras] = useState([]);
    const [recibidos, setRecibidos] = useState([]);
    const [activeTab, setActiveTab] = useState('mis-carreras'); // 'mis-carreras' | 'recibidos'
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    // Modal de gestión de compartición
    const [showModal, setShowModal] = useState(false);
    const [selectedCarrera, setSelectedCarrera] = useState(null);
    const [hermanas, setHermanas] = useState([]);
    const [loadingModal, setLoadingModal] = useState(false);
    const [dragOverColumn, setDragOverColumn] = useState(null);

    // Confirmación autocompartir cursos
    const [confirmAutoShareOpen, setConfirmAutoShareOpen] = useState(false);
    const [confirmAutoShareData, setConfirmAutoShareData] = useState(null);

    const idEmpresaPadre = getSessionEmpresaId();
    const isSuper = isSessionSuperAdmin();
    const effectiveId = isSuper ? 0 : idEmpresaPadre;

    const triggerAlert = (message, variant = 'error', title = 'Error') =>
        setAlertConfig({ open: true, message, variant, title });

    const loadData = async (silent = false) => {
        if (effectiveId === null && !isSuper) return;
        if (!silent) setLoading(true);
        try {
            const [resMis, resRecibidos] = await Promise.all([
                axios.get(ENDPOINTS.carreraEmpresaCompartidaDeHijas(effectiveId)),
                idEmpresaPadre && !isSuper
                    ? axios.get(ENDPOINTS.carreraEmpresaCompartidaRecibidosEnriquecidos(idEmpresaPadre))
                    : Promise.resolve({ data: [] })
            ]);

            const allManageable = (resMis.data || []).map(c => ({ ...c, canManage: true }));
            const receivedEnriched = (resRecibidos.data || []).map(c => ({ ...c, canManage: true }));

            // Separar propios de otros
            const owned = isSuper ? allManageable : allManageable.filter(c => (c.idEmpresa || c.IdEmpresa) === idEmpresaPadre);
            const inheritedOrShared = isSuper ? [] : allManageable.filter(c => (c.idEmpresa || c.IdEmpresa) !== idEmpresaPadre);

            const combinedRecibidos = [...receivedEnriched];
            inheritedOrShared.forEach(fo => {
                if (!combinedRecibidos.some(r => (r.idCarrera || r.IdCarrera) === (fo.idCarrera || fo.IdCarrera))) {
                    combinedRecibidos.push(fo);
                }
            });

            setMisCarreras(owned);
            setRecibidos(combinedRecibidos);
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar la lista de carreras.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [effectiveId]);

    const activeRows = activeTab === 'mis-carreras' ? misCarreras : recibidos;

    const filtered = useMemo(() => {
        const q = query.toLowerCase().trim();
        if (!q) return activeRows;
        return activeRows.filter(r =>
            (r.nombre || r.Nombre || '').toLowerCase().includes(q) ||
            (r.nombreEmpresaPropietaria || r.NombreEmpresaPropietaria || r.nombreEmpresaOrigen || '').toLowerCase().includes(q)
        );
    }, [activeRows, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleManageSharing = async (carrera, silent = false) => {
        setSelectedCarrera(carrera);
        if (!silent) { setShowModal(true); setLoadingModal(true); }
        try {
            const res = await axios.get(ENDPOINTS.carreraEmpresaCompartidaHermanas(carrera.idCarrera || carrera.IdCarrera, effectiveId));
            setHermanas(res.data || []);
        } catch (e) {
            triggerAlert('Error al cargar empresas hermanas.');
        } finally {
            setLoadingModal(false);
        }
    };

    const toggleShare = async (hermana, autoShare = false) => {
        if (hermana.esPropietaria || hermana.EsPropietaria) return;
        try {
            if (hermana.yaCompartido || hermana.YaCompartido) {
                await axios.delete(`${ENDPOINTS.carreraEmpresaCompartida}/${hermana.idCarreraEmpresaCompartida || hermana.IdCarreraEmpresaCompartida}`);
            } else {
                await axios.post(ENDPOINTS.carreraEmpresaCompartida, {
                    IdCarrera: selectedCarrera.idCarrera || selectedCarrera.IdCarrera,
                    IdEmpresaDestino: hermana.idEmpresa || hermana.IdEmpresa,
                    IdEmpresaOrigen: effectiveId,
                    AutoCompartirCursos: autoShare
                });
            }
            handleManageSharing(selectedCarrera, true);
            loadData(true);
        } catch (e) {
            console.error(e);
            const msg = e.response?.data?.message || (typeof e.response?.data === 'string' ? e.response.data : null) || 'Error al modificar la compartición.';
            if (msg.startsWith('MISSING_COURSES:')) {
                const coursesStr = msg.replace('MISSING_COURSES:', '');
                const cursos = coursesStr.split(', ').filter(c => c.trim().length > 0);
                setConfirmAutoShareData({ hermana, cursos });
                setConfirmAutoShareOpen(true);
            } else {
                triggerAlert(msg);
            }
        }
    };

    const handleDragStart = (e, hermana, sourceCol) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ hermana, sourceCol }));
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (e, targetCol) => { e.preventDefault(); setDragOverColumn(targetCol); };
    const handleDrop = (e, targetCol) => {
        e.preventDefault(); setDragOverColumn(null);
        if (selectedCarrera && !(selectedCarrera.idEmpresa || selectedCarrera.IdEmpresa)) return;
        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;
        const { hermana, sourceCol } = JSON.parse(dataStr);
        if (sourceCol === targetCol) return;
        toggleShare(hermana);
    };
    const handleDropDirect = (hermana) => {
        if (selectedCarrera && !(selectedCarrera.idEmpresa || selectedCarrera.IdEmpresa)) return;
        toggleShare(hermana);
    };

    const metrics = useMemo(() => ({
        totalMis: misCarreras.length,
        totalRecibidos: recibidos.length,
        totalDistribuciones: misCarreras.reduce((acc, r) => acc + (r.totalCompartidos || r.TotalCompartidos || 0), 0)
    }), [misCarreras, recibidos]);

    const switchTab = (tab) => { setActiveTab(tab); setQuery(''); setPage(1); };

    return (
        <div className="panel-sigma-main">
            <div className="profile-header-title">
                <h1>Gestión de Carreras Compartidas</h1>
            </div>

            <div className="stats-grid stats-grid-wrap" style={{ marginBottom: '2rem' }}>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiBriefcase size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '2px' }}>Mis Carreras</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{metrics.totalMis}</p>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiInbox size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '2px' }}>Carreras Recibidas</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{metrics.totalRecibidos}</p>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiShare2 size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '2px' }}>Total Distribuciones</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{metrics.totalDistribuciones}</p>
                    </div>
                </div>
            </div>

            <div className="roles-shell">
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0' }}>
                    {[
                        { id: 'mis-carreras', label: `📦 Mis Carreras (${metrics.totalMis})` },
                        { id: 'recibidos',  label: `📥 Recibidas (${metrics.totalRecibidos})` }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => switchTab(tab.id)}
                            style={{
                                padding: '0.5rem 1.2rem',
                                fontSize: '0.9rem',
                                fontWeight: activeTab === tab.id ? 700 : 500,
                                color: activeTab === tab.id ? 'var(--color-primary)' : '#64748b',
                                background: 'none',
                                border: 'none',
                                borderBottom: activeTab === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
                                marginBottom: '-2px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="roles-topbar">
                    <h2 className="roles-title">
                        {activeTab === 'mis-carreras' ? 'Mis Carreras (compartibles)' : 'Carreras Compartidas con Nosotros'}
                    </h2>
                    <input
                        className="roles-search"
                        placeholder="Buscar por carrera o empresa…"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Carrera</th>
                                    <th>{activeTab === 'mis-carreras' ? 'Empresa' : 'Empresa de Origen'}</th>
                                    <th style={{ textAlign: 'center' }}>Empresas con Acceso</th>
                                    {puedeEditar && <th style={{ textAlign: 'center' }}>Gestionar</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={puedeEditar ? 4 : 3} style={{ textAlign: 'center' }}>Cargando...</td></tr>
                                ) : paged.length > 0 ? (
                                    paged.map((r, i) => (
                                        <tr key={r.idCarrera || r.IdCarrera || r.idCarreraEmpresaCompartida || `row-${i}`}>
                                            <td><strong>{r.nombre || r.Nombre}</strong></td>
                                            <td>
                                                <span style={{ fontSize: '0.85rem', color: '#334155' }}>
                                                    {r.nombreEmpresaPropietaria || r.NombreEmpresaPropietaria || r.nombreEmpresaOrigen || 'Global'}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className="cc-chip">
                                                    {(() => {
                                                        const count = r.totalCompartidos !== undefined ? r.totalCompartidos : (r.TotalCompartidos || 0);
                                                        return `${count} ${count === 1 ? 'empresa' : 'empresas'}`;
                                                    })()}
                                                </span>
                                            </td>
                                            {puedeEditar && (
                                                <td style={{ textAlign: 'center' }}>
                                                    {r.canManage && (
                                                        <button
                                                            type="button"
                                                            className="rk-iconbtn rk-iconbtn--edit"
                                                            onClick={() => handleManageSharing(r)}
                                                            title="Gestionar Compartición"
                                                        >
                                                            <img src={sigmaEdit} alt="Share" className="rk-ico" />
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={puedeEditar ? 4 : 3} style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
                                            {activeTab === 'mis-carreras'
                                                ? 'No hay carreras propias para compartir.'
                                                : 'No se han recibido carreras compartidas.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                        <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-action" style={{ padding: '0.3rem 0.8rem' }}>‹</button>
                        <span style={{ lineHeight: '2rem', fontSize: '0.85rem', color: '#64748b' }}>{page} / {totalPages}</span>
                        <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-action" style={{ padding: '0.3rem 0.8rem' }}>›</button>
                    </div>
                )}

                {showModal && (
                    <div className="sigma-modal-overlay">
                        <div className="sigma-modal-content" style={{ maxWidth: '800px', width: '90%' }}>
                            <div className="sigma-modal-header">
                                <h3>Compartir Carrera: {selectedCarrera?.nombre || selectedCarrera?.Nombre}</h3>
                                <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
                            </div>
                            <div className="sigma-modal-body">
                                <p className="text-sm text-gray-500 mb-4">Arrastra las empresas hacia el panel destino para compartir esta carrera.</p>
                                {loadingModal ? (
                                    <p>Cargando empresas...</p>
                                ) : (
                                    <div className="dnd-container" style={{ margin: 0, height: '400px' }}>
                                        <div
                                            className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
                                            onDragOver={(e) => handleDragOver(e, 'available')}
                                            onDrop={(e) => handleDrop(e, 'available')}
                                        >
                                            <div className="dnd-header">Empresas Origen / Sin Acceso</div>
                                            {(() => {
                                                const currentCompany = hermanas.find(x => (x.idEmpresa || x.IdEmpresa) === effectiveId);
                                                const currentParentId = currentCompany ? (currentCompany.idPadre || currentCompany.IdPadre) : null;
                                                
                                                const checkModifiable = (h) => {
                                                    if (isSuper) return true;
                                                    const hid = h.idEmpresa || h.IdEmpresa;
                                                    const hpadre = h.idPadre || h.IdPadre;
                                                    return hid === effectiveId || hpadre === effectiveId || (currentParentId !== null && hid === currentParentId);
                                                };

                                                const available = hermanas.filter(h => !(h.yaCompartido || h.YaCompartido) && !(h.esPropietaria || h.EsPropietaria) && checkModifiable(h));
                                                if (available.length === 0) return <div className="dnd-empty">No hay más empresas.</div>;
                                                return available.map((h, i) => (
                                                    <div key={h.idEmpresa || h.IdEmpresa || `av-${i}`} className="dnd-item" draggable onDragStart={(e) => handleDragStart(e, h, 'available')}>
                                                        <span>{h.nombre || h.Nombre}</span>
                                                        <button type="button" onClick={() => handleDropDirect(h)} style={{ background: 'none', border: 'none', fontSize: '1.2em', color: 'var(--color-success)', cursor: 'pointer', padding: '0 5px' }} title="Conceder Acceso">＋</button>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                        <div
                                            className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
                                            onDragOver={(e) => handleDragOver(e, 'assigned')}
                                            onDrop={(e) => handleDrop(e, 'assigned')}
                                        >
                                            <div className="dnd-header">Distribuciones Autorizadas (Destino)</div>
                                            {(() => {
                                                const currentCompany = hermanas.find(x => (x.idEmpresa || x.IdEmpresa) === effectiveId);
                                                const currentParentId = currentCompany ? (currentCompany.idPadre || currentCompany.IdPadre) : null;
                                                
                                                const checkModifiable = (h) => {
                                                    if (isSuper) return true;
                                                    const hid = h.idEmpresa || h.IdEmpresa;
                                                    const hpadre = h.idPadre || h.IdPadre;
                                                    return hid === effectiveId || hpadre === effectiveId || (currentParentId !== null && hid === currentParentId);
                                                };

                                                const assigned = hermanas.filter(h => {
                                                    const isOwner = h.esPropietaria || h.EsPropietaria;
                                                    if (isOwner) return true;
                                                    const isShared = h.yaCompartido || h.YaCompartido;
                                                    if (!isShared) return false;
                                                    return checkModifiable(h);
                                                });
                                                if (assigned.length === 0) return <div className="dnd-empty">Arrastra empresas aquí para compartir.</div>;
                                                return assigned.map((h, i) => {
                                                    const isOwner = h.esPropietaria || h.EsPropietaria;
                                                    return (
                                                        <div
                                                            key={h.idEmpresa || h.IdEmpresa || `as-${i}`}
                                                            className={`dnd-item ${isOwner ? 'dnd-item-disabled' : ''}`}
                                                            draggable={!isOwner}
                                                            onDragStart={(e) => !isOwner && handleDragStart(e, h, 'assigned')}
                                                            style={isOwner ? { backgroundColor: '#f1f5f9', color: '#94a3b8', border: '1px dashed #cbd5e1', cursor: 'not-allowed' } : {}}
                                                        >
                                                            <span>
                                                                {h.nombre || h.Nombre}
                                                                {isOwner && <span style={{ marginLeft: '8px', fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-primary)' }}>(PROPIETARIA)</span>}
                                                            </span>
                                                            {!isOwner && (
                                                                <button type="button" onClick={() => handleDropDirect(h)} style={{ background: 'none', border: 'none', fontSize: '1.2em', color: 'var(--color-danger)', cursor: 'pointer', padding: '0 5px' }} title="Revocar Acceso">×</button>
                                                            )}
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="sigma-modal-footer">
                                <button className="btn-action" onClick={() => setShowModal(false)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                )}

                <AlertDialog
                    open={alertConfig.open}
                    title={alertConfig.title}
                    message={alertConfig.message}
                    variant={alertConfig.variant}
                    onClose={() => setAlertConfig({ ...alertConfig, open: false })}
                />

                {confirmAutoShareOpen && confirmAutoShareData && (
                    <div className="roles-confirm-overlay" role="dialog" aria-modal="true" style={{ zIndex: 1100 }}>
                        <div className="roles-confirm-card" style={{ maxWidth: '500px', width: '90%', background: '#ffffff', padding: '24px', borderRadius: '12px' }}>
                            <div className="roles-confirm-title" style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <FiShare2 /> Compartir Cursos Automáticamente
                            </div>
                            <div className="roles-confirm-sub" style={{ fontSize: '0.95rem', color: '#334155', marginBottom: '16px', lineHeight: '1.5' }}>
                                Para poder cursar la carrera en <strong>{confirmAutoShareData.hermana?.nombre || confirmAutoShareData.hermana?.Nombre}</strong>, es necesario que la empresa tenga acceso a todos sus cursos.
                            </div>
                            
                            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                                <p style={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cursos por compartir:</p>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#1e293b', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                    {confirmAutoShareData.cursos.map((c, idx) => (
                                        <li key={idx}><strong>{c}</strong></li>
                                    ))}
                                </ul>
                            </div>

                            <div className="roles-confirm-sub" style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', marginBottom: '20px' }}>
                                ¿Desea compartir automáticamente estos cursos y continuar?
                            </div>

                            <div className="roles-confirm-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button 
                                    type="button" 
                                    className="btn-cancel" 
                                    style={{ padding: '0.5rem 1.2rem', borderRadius: '6px', cursor: 'pointer' }}
                                    onClick={() => {
                                        setConfirmAutoShareOpen(false);
                                        setConfirmAutoShareData(null);
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="btn-action"
                                    style={{ padding: '0.5rem 1.2rem', borderRadius: '6px', cursor: 'pointer' }}
                                    onClick={async () => {
                                        const { hermana } = confirmAutoShareData;
                                        setConfirmAutoShareOpen(false);
                                        setConfirmAutoShareData(null);
                                        await toggleShare(hermana, true);
                                    }}
                                >
                                    Compartir y Continuar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .cc-chip { background: #f1f5f9; color: #475569; padding: 4px 10px; border-radius: 999px; font-size: 0.8rem; font-weight: 600; }
                .sigma-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
                .sigma-modal-content { background: white; border-radius: 12px; width: 90%; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
                .sigma-modal-header { padding: 20px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
                .sigma-modal-body { padding: 20px; }
                .sigma-modal-footer { padding: 15px 20px; border-top: 1px solid #eee; text-align: right; }
                .close-btn { background: none; border: none; font-size: 24px; cursor: pointer; color: #94a3b8; }
            `}</style>
        </div>
    );
}
